import { classMap } from 'lit/directives/class-map.js'
import { html, literal, unsafeStatic, StaticValue } from 'lit/static-html.js'
import { property } from 'lit/decorators.js'
import { nothing } from 'lit'
import { ifDefined } from 'lit/directives/if-defined.js'

import { WithFocus, WithLang, compose } from '#utils/mixins'
import { LfuiElement } from '#utils/element'

import styles from './lfds-alert.css.ts'

const SECTION = literal`section`
const DIV = literal`div`

const translations = {
	sv: {
		close: 'Stäng',
		info: 'Informationsmeddelande',
		warning: 'Varningsmeddelande',
		error: 'Felmeddelande',
		success: ''
	},
	en: {
		close: 'Close',
		info: 'Information message',
		warning: 'Warning message',
		error: 'Error message',
		success: ''
	}
}

/**
 * Alert component for contextual feedback.
 *
 * ### Language support
 *
 * Built-in labels (close button, screen reader variant text) are translated
 * based on the resolved language. Set `lang="en"` on the component or any
 * ancestor element. Supported: `sv` (default), `en`.
 *
 * @name LfdsAlert
 * @status ready
 * @tag lfds-alert
 *
 * @slot default - Main text content (not the heading).
 * @slot button - Optional action button. Recommended: `<lfds-button slot="button" variant="secondary" size="medium">`.
 */
export default class LfdsAlert extends compose(
	LfuiElement,
	WithFocus,
	WithLang
) {
	static styles = [LfuiElement.styles, styles]
	static events = ['close']

	/** Visual intent and icon. */
	@property() variant: 'info' | 'warning' | 'error' | 'success' = 'info'

	/**
	 * Structure:
	 * - `alert`: rendered as `section`, dismissible
	 * - `info-box`: rendered as `div`, non-dismissible
	 */
	@property() type: 'alert' | 'info-box' = 'alert'

	/** Optional heading shown above body text. */
	@property() heading: string | undefined

	/** Custom screen reader prefix text before content. */
	@property({ attribute: 'sr-variant-text' }) srVariantText?: string

	/** Optional index appended to screen reader prefix. */
	@property({ attribute: 'sr-variant-index' }) srVariantIndex?: string

	/** Disable the screen reader variant prefix. */
	@property({
		attribute: 'sr-variant-disabled',
		type: Boolean
	})
	srVariantDisabled = false

	/** Semantic heading level when heading is provided. */
	@property({ attribute: 'heading-level' }) headingLevel:
		'h1' | 'h2' | 'h3' | 'h4' = 'h2'

	get #headingTag(): StaticValue {
		return unsafeStatic(this.headingLevel)
	}

	#getIconId() {
		switch (this.variant) {
			case 'info':
				return 'chat'
			case 'warning':
				return 'warning'
			case 'error':
				return 'alert'
			case 'success':
				return 'check-enclosed'
		}
	}

	/** Programmatic close API. */
	close() {
		this.#onclose()
	}

	/** Emits composed close event for host apps. */
	#onclose() {
		this.dispatchEvent(
			new CustomEvent('close', {
				bubbles: true,
				composed: true
			})
		)
	}

	#resolvedSrVariant() {
		const variant = this.srVariantText || this.t(translations, this.variant)
		const index = this.srVariantIndex ? ` ${this.srVariantIndex}` : ''

		return `${variant}${index}`
	}

	#screenReaderVariant(heading?: boolean) {
		if (this.srVariantDisabled || (this.heading && !heading)) {
			return nothing
		}

		return html`<span class="sr-only">${this.#resolvedSrVariant()}: </span>`
	}

	render() {
		const tag = this.type === 'alert' ? SECTION : DIV

		return html`
      <${tag}
        lang=${ifDefined(this.lang)}
        class=${classMap({
					wrapper: true,
					[this.type]: this.type,
					[this.variant]: this.variant,
					['focus-visible']: this.focusVisible
				})}
        aria-label=${
					this.type === 'alert' ? this.t(translations, this.variant) : nothing
				}>
        <lfds-icon
          icon-id=${this.#getIconId()}
          color="currentColor"
          size="24"
          class="icon"></lfds-icon>

        <div class="content">
          ${
						this.heading
							? html`
									<${this.#headingTag}
										class="heading"
										tabindex="-1"
									>
										${this.#screenReaderVariant(true)}${this.heading}
									</${this.#headingTag}>
								`
							: nothing
					}

          <p class="body" tabindex="-1">
            ${this.#screenReaderVariant()}
            <slot></slot>
          </p>
        </div>

        <slot name="button"></slot>

        ${
					this.type === 'alert'
						? html`
								<button
									class=${classMap({
										close: true,
										['focus-visible']: this.focusVisible
									})}
									aria-label=${`${this.t(translations, 'close')} ${this.#resolvedSrVariant()}`}
									@click=${this.#onclose}
								>
									<div class="icon-container">
										<lfds-icon
											icon-id="cross-big"
											color="currentColor"
											size="24"
										></lfds-icon>
									</div>
								</button>
							`
						: nothing
				}
      </${tag}>
    `
	}
}
